import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { LoginPage } from './pages/LoginPage';
import { Layout } from './components/Layout';
import { RequirePermission } from './components/RequirePermission';

const PageFallback = () => (
  <div className="flex h-full min-h-[50vh] items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-teal-600" aria-label="Cargando" />
  </div>
);

const DashboardPage = lazy(() =>
  import('./features/dashboard/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const ReportsPage = lazy(() =>
  import('./features/reporting/pages/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const SalesPage = lazy(() =>
  import('./features/sales/pages/SalesPage').then((m) => ({ default: m.SalesPage })),
);
const SaleDetailPage = lazy(() =>
  import('./features/sales/pages/SaleDetailPage').then((m) => ({ default: m.SaleDetailPage })),
);
const SalePrintPage = lazy(() =>
  import('./features/sales/pages/SalePrintPage').then((m) => ({ default: m.SalePrintPage })),
);
const CreditsPage = lazy(() =>
  import('./features/credits/pages/CreditsPage').then((m) => ({ default: m.CreditsPage })),
);
const CreditDetailPage = lazy(() =>
  import('./features/credits/pages/CreditDetailPage').then((m) => ({
    default: m.CreditDetailPage,
  })),
);
const ProfitabilityPage = lazy(() =>
  import('./features/reporting/pages/ProfitabilityPage').then((m) => ({
    default: m.ProfitabilityPage,
  })),
);
const ProductsPage = lazy(() =>
  import('./features/products/pages/ProductsPage').then((m) => ({ default: m.ProductsPage })),
);
const CategoriesPage = lazy(() =>
  import('./features/products/pages/CategoriesPage').then((m) => ({ default: m.CategoriesPage })),
);
const ProductDetailPage = lazy(() =>
  import('./features/products/pages/ProductDetailPage').then((m) => ({
    default: m.ProductDetailPage,
  })),
);
const CustomersPage = lazy(() =>
  import('./features/customers/pages/CustomersPage').then((m) => ({ default: m.CustomersPage })),
);
const SuppliersPage = lazy(() =>
  import('./features/suppliers/pages/SuppliersPage').then((m) => ({ default: m.SuppliersPage })),
);
const SupplierDetailPage = lazy(() =>
  import('./features/suppliers/pages/SupplierDetailPage').then((m) => ({
    default: m.SupplierDetailPage,
  })),
);
const PurchasesPage = lazy(() =>
  import('./features/purchases/pages/PurchasesPage').then((m) => ({ default: m.PurchasesPage })),
);
const NewPurchasePage = lazy(() =>
  import('./features/purchases/pages/NewPurchasePage').then((m) => ({
    default: m.NewPurchasePage,
  })),
);
const TeamPage = lazy(() =>
  import('./features/team/pages/TeamPage').then((m) => ({ default: m.TeamPage })),
);
const TeamDetailPage = lazy(() =>
  import('./features/team/pages/TeamDetailPage').then((m) => ({ default: m.TeamDetailPage })),
);
const SupplierPaymentsPage = lazy(() =>
  import('./features/supplier-payments/pages/SupplierPaymentsPage').then((m) => ({
    default: m.SupplierPaymentsPage,
  })),
);
const CashSessionsPage = lazy(() =>
  import('./features/cash/pages/CashSessionsPage').then((m) => ({
    default: m.CashSessionsPage,
  })),
);
const ExchangeRatesPage = lazy(() =>
  import('./features/rates/pages/ExchangeRatesPage').then((m) => ({
    default: m.ExchangeRatesPage,
  })),
);
const SettingsPage = lazy(() =>
  import('./features/settings/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const VentasPorVendedorPage = lazy(() =>
  import('./features/reporting/pages/VentasPorVendedorPage').then((m) => ({
    default: m.VentasPorVendedorPage,
  })),
);
const CommissionsPage = lazy(() =>
  import('./features/commissions/pages/CommissionsPage').then((m) => ({
    default: m.CommissionsPage,
  })),
);
const WarehousesPage = lazy(() =>
  import('./features/inventory/pages/WarehousesPage').then((m) => ({ default: m.WarehousesPage })),
);
const WarehouseDetailPage = lazy(() =>
  import('./features/inventory/pages/WarehouseDetailPage').then((m) => ({
    default: m.WarehouseDetailPage,
  })),
);
const MovementsPage = lazy(() =>
  import('./features/inventory/pages/MovementsPage').then((m) => ({ default: m.MovementsPage })),
);
const InventoryClosePage = lazy(() =>
  import('./features/inventory/pages/InventoryClosePage').then((m) => ({
    default: m.InventoryClosePage,
  })),
);
const StockPage = lazy(() =>
  import('./features/inventory/pages/StockPage').then((m) => ({ default: m.StockPage })),
);
const ExpensesPage = lazy(() =>
  import('./features/expenses/pages/ExpensesPage').then((m) => ({ default: m.ExpensesPage })),
);
const AuditPage = lazy(() =>
  import('./features/audit/pages/AuditPage').then((m) => ({ default: m.AuditPage })),
);
const AuditDetailPage = lazy(() =>
  import('./features/audit/pages/AuditDetailPage').then((m) => ({ default: m.AuditDetailPage })),
);
const AuditAnomaliesPage = lazy(() =>
  import('./features/audit/pages/AuditAnomaliesPage').then((m) => ({
    default: m.AuditAnomaliesPage,
  })),
);
const BootstrapTokens = lazy(() =>
  import('./pages/BootstrapTokens').then((m) => ({ default: m.BootstrapTokens })),
);

export default function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<Layout />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/sales" element={<SalesPage />} />
          <Route path="/sales/:id" element={<SaleDetailPage />} />
          <Route path="/sales/:id/print" element={<SalePrintPage />} />
          <Route
            path="/credits"
            element={
              <RequirePermission permissions={['CREDITOS_VER']}>
                <CreditsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/credits/cliente/:clienteId"
            element={
              <RequirePermission permissions={['CREDITOS_VER']}>
                <CreditDetailPage />
              </RequirePermission>
            }
          />
          <Route path="/reports/profitability" element={<ProfitabilityPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/categories" element={<CategoriesPage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/suppliers" element={<SuppliersPage />} />
          <Route path="/suppliers/:id" element={<SupplierDetailPage />} />
          <Route path="/purchases" element={<PurchasesPage />} />
          <Route path="/purchases/new" element={<NewPurchasePage />} />
          <Route
            path="/supplier-payments"
            element={
              <RequirePermission permissions={['PROVEEDORES_VER']}>
                <SupplierPaymentsPage />
              </RequirePermission>
            }
          />
          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path="/commissions" element={<CommissionsPage />} />
          <Route path="/cash-sessions" element={<CashSessionsPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/team/:id" element={<TeamDetailPage />} />
          <Route path="/users" element={<Navigate to="/team" replace />} />
          <Route path="/exchange-rates" element={<ExchangeRatesPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/warehouses" element={<WarehousesPage />} />
          <Route path="/warehouses/:id" element={<WarehouseDetailPage />} />
          <Route path="/stock" element={<StockPage />} />
          <Route path="/movements" element={<MovementsPage />} />
          <Route path="/inventory/close" element={<InventoryClosePage />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/audit/:entidad/:entidad_id" element={<AuditDetailPage />} />
          <Route path="/audit/anomalies" element={<AuditAnomaliesPage />} />
          <Route path="/bootstrap-tokens" element={<BootstrapTokens />} />
          <Route path="/reports/ventas-por-vendedor" element={<VentasPorVendedorPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
